import { OfferRentAmendments } from '@features/amendments/components/OfferRentAmendments'
import { ContractClausesEditor } from '@features/contracts/components/ContractClausesEditor'
import { ContractDraftHistory } from '@features/contracts/components/ContractDraftHistory'
import { ContractExtensionHistory } from '@features/offers/components/ContractExtensionHistory'
import { ExtendContractDialog } from '@features/offers/components/ExtendContractDialog'
import { TenantRenewalAnswer } from '@features/offers/components/TenantRenewalAnswer'
import { TerminateContractDialog } from '@features/offers/components/TerminateContractDialog'
import { useOfferContractPreview } from '@services/contracts/mutations/useOfferContractPreview'
import { useSendOfferContractDraft } from '@services/contracts/mutations/useSendOfferContractDraft'
import { useContracts } from '@services/contracts/queires/useContracts'
import { useRent } from '@services/contracts/queires/useRent'
import { useChangeStatus } from '@services/offers/mutations/useChangeStatus'
import { useUpdateOffer } from '@services/offers/mutations/useUpdateOffer'
import type { ApiError } from '@services/utils/ApiError'
import { addYears, format, parseISO } from 'date-fns'
import {
  Activity,
  ArrowLeft,
  Check,
  Eye,
  FileClock,
  FilePen,
  Send,
  ShieldCheck
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { OfferWrite } from 'types/api'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

interface OfferActionProps {
  status: string
  id: string
  hasInspection?: boolean
}

const STEPS = ['CREATED', 'INTERESTED', 'ACCEPTED', 'RESERVED', 'ONGOING']
const STEP_INDEX: Record<string, number> = {
  CREATED: 0,
  INTERESTED: 1,
  ACCEPTED: 2,
  RESERVED: 3,
  ONGOING: 4,
  NOTICE_PERIOD: 4,
  TERMINATED: 5
}

function StatusStepper({ status }: { status?: string }) {
  const { t } = useTranslation('offers')
  const dead = status === 'REJECTED' || status === 'CANCELED'
  const current = status ? STEP_INDEX[status] ?? -1 : -1

  return (
    <div className="space-y-3">
      {dead && (
        <div className="rounded-xl bg-destructive/10 px-3 py-2 text-sm font-semibold text-destructive">
          {t(`statuses.${status}`)}
        </div>
      )}
      <div className="flex">
        {STEPS.map((step, i) => {
          const done = i < current
          const isCur = i === current
          return (
            <div
              key={step}
              className="relative flex flex-1 flex-col items-center"
            >
              {i < STEPS.length - 1 && (
                <span
                  className={cn(
                    'absolute left-1/2 top-4 z-0 h-0.5 w-full',
                    i < current ? 'bg-primary' : 'bg-ink/10'
                  )}
                />
              )}
              <span
                className={cn(
                  'relative z-10 flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-colors',
                  done
                    ? 'bg-primary text-white'
                    : isCur
                      ? 'border-2 border-primary bg-white text-primary'
                      : 'border border-ink/15 bg-white text-ink/40'
                )}
              >
                {done ? <Check className="h-4 w-4" /> : i + 1}
              </span>
              <span
                className={cn(
                  'mt-2.5 px-0.5 text-center text-[11px] font-medium leading-tight',
                  isCur ? 'text-ink' : 'text-muted-foreground'
                )}
              >
                {t(`statuses.${step}`)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ErrorPills({ messages }: { messages: string[] }) {
  const { t } = useTranslation('backend')
  if (!messages.length) return null
  return (
    <div className="flex flex-wrap gap-1.5">
      {messages.map((m, i) => (
        <span
          key={`${m}-${i}`}
          className="rounded-full bg-destructive/10 px-2.5 py-0.5 text-xs font-semibold text-destructive"
        >
          {t(m)}
        </span>
      ))}
    </div>
  )
}

const StatusCreate = ({ id }: { id: string }) => {
  const { mutate: changeStatus } = useChangeStatus()
  const { t } = useTranslation('offers')
  return (
    <div className="flex flex-col items-start gap-6">
      <p className="m-0 text-sm leading-relaxed text-muted-foreground">
        {t('admin.actions.hints.created')}
      </p>
      <Button
        onClick={() => changeStatus({ id, body: { status: 'INTERESTED' } })}
      >
        {t('admin.actions.change_to_interested')}
      </Button>
    </div>
  )
}

const StatusInterested = ({
  id,
  hasInspection
}: {
  id: string
  hasInspection?: boolean
}) => {
  const { error, mutate: changeStatus } = useChangeStatus()
  const { mutate: updateOffer } = useUpdateOffer()
  const { t } = useTranslation('offers')
  const { confirm, dialog } = useConfirm()
  const message = (error as any)?.source?.response?.data?.message as
    | string
    | undefined
  return (
    <div className="flex flex-col items-start gap-6">
      {dialog}
      <p className="m-0 text-sm leading-relaxed text-muted-foreground">
        {t('admin.actions.hints.interested')}
      </p>
      <ErrorPills messages={message ? [message] : []} />

      <Card
        variant="light"
        className="flex w-full items-center justify-between gap-3 px-4 py-3"
      >
        <Label className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          {t('admin.edit_modal.hasInspection')}
        </Label>
        <Switch
          checked={!!hasInspection}
          onCheckedChange={(c) =>
            updateOffer({ id, body: { has_inspection: c } as OfferWrite })
          }
        />
      </Card>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="ghostDark"
          onClick={async () => {
            if (
              await confirm({
                title: t('admin.actions.confirm_back', {
                  state: t('statuses.CREATED')
                }),
                confirmText: t('admin.form.yes'),
                cancelText: t('admin.form.no')
              })
            ) {
              changeStatus({ id, body: { status: 'CREATED' } })
            }
          }}
        >
          <ArrowLeft className="h-4 w-4" />
          {t('admin.actions.back')}
        </Button>
        <Button
          onClick={() => changeStatus({ id, body: { status: 'ACCEPTED' } })}
          className="flex-1"
        >
          {t('admin.actions.change_to_accepted')}
        </Button>
      </div>
    </div>
  )
}

const StatusAccepted = ({ id }: { id: string }) => {
  const { error: changeStatusError, mutate: changeStatus } = useChangeStatus()
  const { error: previewError, mutate: showPreview } = useOfferContractPreview()
  const { isPending: isSendingDraft, mutate: sendDraft } =
    useSendOfferContractDraft()
  const { t } = useTranslation('offers')

  const [startDate, setStartDate] = useState<string | undefined>()
  const [endDate, setEndDate] = useState<string | undefined>()
  const [snaFee, setSnaFee] = useState(true)
  const [editorOpen, setEditorOpen] = useState(false)
  const { t: tClauses } = useTranslation('clauses')
  const [validationErrors, setValidationErrors] = useState<{
    users: Array<{ email: string; role: string; errors: Record<string, string> }>
    flat: Record<string, string>
  } | null>(null)

  useEffect(() => {
    const error = changeStatusError || previewError
    if (!error) {
      setValidationErrors(null)
      return
    }
    const apiError = error as ApiError
    if (
      apiError.message === 'backend.offer_user_incomplete_profile_error' &&
      apiError.extra
    ) {
      const errors = apiError.extra as {
        users?: Array<{
          email: string
          role: string
          errors: Record<string, string>
        }>
        flat?: Record<string, string>
      }
      setValidationErrors({ users: errors.users || [], flat: errors.flat || {} })
    } else {
      setValidationErrors(null)
    }
  }, [changeStatusError, previewError])

  const onStartChange = (value?: string) => {
    setStartDate(value)
    setEndDate(
      value ? format(addYears(parseISO(value), 1), 'yyyy-MM-dd') : undefined
    )
  }

  const handlePreview = () => {
    if (!startDate || !endDate) return
    showPreview({
      id,
      params: {
        action: 'view',
        start_date: startDate,
        end_date: endDate,
        sna_fee: snaFee
      }
    })
  }

  const handleSendDraft = async () => {
    if (!startDate || !endDate) return
    // Potvrdenie je tu naschvál: odchádza e-mail klientom a ten sa nedá
    // odvolať.
    if (
      !(await confirm({
        title: t('admin.actions.confirm_send_draft'),
        description: t('admin.actions.send_draft_description'),
        confirmText: t('admin.form.yes'),
        cancelText: t('admin.form.no')
      }))
    )
      return
    sendDraft({
      offer_id: id,
      start_date: startDate,
      end_date: endDate,
      sna_fee: snaFee
    })
  }

  const handleConfirm = () => {
    if (!startDate || !endDate) return
    changeStatus({
      id,
      body: {
        start_date: startDate,
        end_date: endDate,
        status: 'RESERVED',
        sna_fee: snaFee
      }
    })
  }

  const { t: tBackend } = useTranslation('backend')
  const { confirm, dialog } = useConfirm()

  return (
    <div className="flex w-full flex-col gap-5">
      {dialog}
      <p className="m-0 text-sm leading-relaxed text-muted-foreground">
        {t('admin.actions.hints.accepted')}
      </p>
      {validationErrors && (
        <div className="space-y-3">
          {validationErrors.users.map((user, index) => (
            <div key={`${user.email}-${index}`} className="space-y-1.5">
              <p className="m-0 text-sm font-semibold text-destructive">
                {tBackend(`backend.${user.role}.escape`, { email: user.email })}
              </p>
              <ErrorPills messages={Object.values(user.errors)} />
            </div>
          ))}
          {Object.keys(validationErrors.flat).length > 0 && (
            <div className="space-y-1.5">
              <p className="m-0 text-sm font-semibold text-destructive">
                {t('admin.actions.flat_missing_fields')}
              </p>
              <ErrorPills messages={Object.values(validationErrors.flat)} />
            </div>
          )}
        </div>
      )}

      <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label className="mb-1.5 block">
            {t('admin.form.start_date')}
            <span className="ml-0.5 text-destructive">*</span>
          </Label>
          <DatePicker
            value={startDate}
            onChange={onStartChange}
            min={format(new Date(), 'yyyy-MM-dd')}
          />
        </div>
        <div>
          <Label className="mb-1.5 block">
            {t('admin.form.end_date')}
            <span className="ml-0.5 text-destructive">*</span>
          </Label>
          <DatePicker value={endDate} onChange={setEndDate} />
        </div>
      </div>

      <Card
        variant="light"
        className="flex w-full items-center justify-between gap-3 px-4 py-3"
      >
        <Label>{t('admin.form.sna_fee')}</Label>
        <Switch checked={snaFee} onCheckedChange={setSnaFee} />
      </Card>

      <div className="w-full space-y-2">
        <Button
          variant="ghostDark"
          onClick={handlePreview}
          disabled={!startDate || !endDate}
          className="w-full"
        >
          <Eye className="h-4 w-4" />
          {t('admin.actions.preview_contract')}
        </Button>
        <Button
          variant="ghostDark"
          onClick={() => setEditorOpen(true)}
          disabled={!startDate || !endDate}
          className="w-full"
        >
          <FilePen className="h-4 w-4" />
          {tClauses('open_editor')}
        </Button>
        {startDate && endDate && (
          <ContractClausesEditor
            open={editorOpen}
            onOpenChange={setEditorOpen}
            target={{
              kind: 'offer',
              offerId: id,
              startDate,
              endDate,
              snaFee
            }}
          />
        )}
        <Button
          variant="ghostDark"
          onClick={handleSendDraft}
          disabled={!startDate || !endDate || isSendingDraft}
          className="w-full"
        >
          <Send className="h-4 w-4" />
          {t('admin.actions.send_draft')}
        </Button>
        <ContractDraftHistory target={{ kind: 'offer', offerId: id }} />
        <div className="flex gap-2">
          <Button
            variant="ghostDark"
            onClick={async () => {
              if (
                await confirm({
                  title: t('admin.actions.confirm_back', {
                    state: t('statuses.INTERESTED')
                  }),
                  confirmText: t('admin.form.yes'),
                  cancelText: t('admin.form.no')
                })
              ) {
                changeStatus({ id, body: { status: 'INTERESTED' } })
              }
            }}
          >
            <ArrowLeft className="h-4 w-4" />
            {t('admin.actions.back')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!startDate || !endDate}
            className="flex-1"
          >
            {t('admin.actions.confirm_reserved')}
          </Button>
        </div>
      </div>
    </div>
  )
}

const StatusReserved = ({ id }: { id: string }) => {
  const { t } = useTranslation('offers')
  return (
    <div className="flex w-full flex-col gap-6">
      <OfferSummary id={id} />
      <ResendContract offerId={id} />
      <ContractDraftHistory target={{ kind: 'offer', offerId: id }} />
      <p className="m-0 text-sm text-muted-foreground">
        {t('admin.actions.waiting_for_user_payment_and_contract')}
      </p>
    </div>
  )
}

// Contract rows that still wait for signatures; anything else means somebody
// signed (or declined) and a new version is no longer possible.
const RESENDABLE_STATUSES = ['awaiting', 'sent', 'opened']

/** Edit the points of a contract out for signing and send a new version. */
function ResendContract({ offerId }: { offerId: string }) {
  const { t } = useTranslation('clauses')
  const [open, setOpen] = useState(false)
  const { data: contracts } = useContracts({
    params: { offer: offerId, type: 'offer' }
  })

  const rows = contracts?.results ?? []
  const main = rows[0] as
    | (NonNullable<typeof rows[number]> & { sna_fee?: boolean | null })
    | undefined
  const unsigned =
    rows.length > 0 &&
    rows.every((row) => RESENDABLE_STATUSES.includes(row.status ?? ''))
  if (!main?.id || !main.start_date || !main.end_date || !unsigned) return null

  return (
    <>
      <Button variant="ghostDark" onClick={() => setOpen(true)} className="w-full">
        <FilePen className="h-4 w-4" />
        {t('open_editor_resend')}
      </Button>
      <ContractClausesEditor
        open={open}
        onOpenChange={setOpen}
        target={{
          kind: 'offer',
          offerId,
          startDate: main.start_date,
          endDate: main.end_date,
          snaFee: main.sna_fee ?? true
        }}
        resend={{
          contractId: main.id,
          type: 'offer',
          askSnaFee: main.sna_fee === null || main.sna_fee === undefined
        }}
      />
    </>
  )
}

const INITIAL_PAYMENT_TYPES = ['DEPOSIT', 'RENT', 'ADMINISTRATIVE_FEE']
const SIGNED_STATUSES = ['completed', 'signed']

type SignatureInfo = {
  name?: string
  email?: string
  represented_by?: string | null
  status?: string
}

function signatureStyle(status?: string) {
  if (SIGNED_STATUSES.includes(status ?? ''))
    return { key: 'sig_signed', cls: 'bg-primary/10 text-primary' }
  if (status === 'declined')
    return { key: 'sig_declined', cls: 'bg-destructive/10 text-destructive' }
  return { key: 'sig_pending', cls: 'bg-peach/15 text-peach' }
}

function OfferSummary({ id }: { id: string }) {
  const { t } = useTranslation('offers')
  const { data: contracts } = useContracts({
    params: { offer: id, type: 'offer' }
  })
  // All rents: the initial payments are the oldest, past any first page.
  const { data: rents } = useRent({ id, limit: -1 })

  const contract =
    contracts?.results?.find((c) => SIGNED_STATUSES.includes(c.status ?? '')) ??
    contracts?.results?.[0]
  const signatures =
    (contract as unknown as { signatures?: SignatureInfo[] } | undefined)
      ?.signatures ?? []
  const isSigned =
    signatures.length > 0
      ? signatures.every((s) => SIGNED_STATUSES.includes(s.status ?? ''))
      : !!contract && SIGNED_STATUSES.includes(contract.status ?? '')

  const clientRents = (rents?.results ?? []).filter((r) =>
    INITIAL_PAYMENT_TYPES.includes(r.payment_type ?? '')
  )
  const earliest = clientRents.reduce<string | undefined>(
    (min, r) => (!min || r.due_date < min ? r.due_date : min),
    undefined
  )
  const initial = clientRents
    .filter((r) => r.due_date === earliest)
    .sort((a, b) => (a.payment_type ?? '').localeCompare(b.payment_type ?? ''))

  if (!contract && initial.length === 0) return null

  const fmt = (d?: string | null) => (d ? format(parseISO(d), 'dd.MM.yyyy') : '—')

  return (
    <div className="w-full space-y-4">
      {contract && (
        <Card variant="light" className="space-y-1 p-4">
          <div className="flex flex-wrap items-center gap-x-2 text-sm font-semibold text-ink">
            {isSigned ? (
              <ShieldCheck className="h-4 w-4 text-primary" />
            ) : (
              <FileClock className="h-4 w-4 text-peach" />
            )}
            {isSigned
              ? t('actions.summary.contract_signed')
              : t('actions.summary.contract_awaiting')}
            {contract.contract_number && (
              <span className="font-normal text-muted-foreground">
                · {contract.contract_number}
              </span>
            )}
          </div>
          {(contract.start_date || contract.end_date) && (
            <div className="pl-6 text-sm text-muted-foreground">
              {fmt(contract.start_date)} – {fmt(contract.end_date)}
            </div>
          )}
          {signatures.length > 0 && (
            <div className="space-y-1.5 pt-2">
              <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t('actions.summary.signatures')}
              </div>
              {signatures.map((s, i) => {
                const style = signatureStyle(s.status)
                return (
                  <div
                    key={`${s.email}-${i}`}
                    className="flex items-center justify-between gap-2 text-sm"
                  >
                    <span className="min-w-0 truncate text-ink">
                      {s.name}
                      {s.represented_by && (
                        <span className="text-muted-foreground">
                          {' '}
                          · v zast. {s.represented_by}
                        </span>
                      )}
                    </span>
                    <span
                      className={cn(
                        'shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold',
                        style.cls
                      )}
                    >
                      {t(`actions.summary.${style.key}`)}
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </Card>
      )}

      {initial.length > 0 && (
        <div className="space-y-2">
          <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {t('actions.summary.first_payments')}
          </div>
          <div className="space-y-1.5">
            {initial.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between gap-2 rounded-xl border border-ink/[0.06] bg-white px-3 py-2 text-sm"
              >
                <span className="text-ink">
                  {r.payment_type === 'RENT' &&
                  (r.variable_symbol ?? '').startsWith('10')
                    ? t('actions.summary.first_payment')
                    : t(`actions.summary.${r.payment_type}`)}
                </span>
                <span className="flex items-center gap-2">
                  <span className="font-semibold text-ink">
                    {Number(r.amount ?? 0).toFixed(2)} €
                  </span>
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-xs font-semibold',
                      r.paid ? 'bg-primary/10 text-primary' : 'bg-peach/15 text-peach'
                    )}
                  >
                    {r.paid
                      ? t('actions.summary.paid')
                      : t('actions.summary.unpaid')}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const StatusOngoing = ({ id }: { id: string }) => {
  const { t } = useTranslation('offers')
  return (
    <div className="flex flex-col items-start gap-6">
      <OfferSummary id={id} />
      <TenantRenewalAnswer offerId={id} />
      <ContractExtensionHistory offerId={id} />
      <p className="m-0 text-sm leading-relaxed text-muted-foreground">
        {t('actions.contract_ongoing')}
      </p>
      <div className="flex flex-wrap gap-3">
        <ExtendContractDialog offerId={id} />
        <TerminateContractDialog offerId={id} />
      </div>
      <div className="w-full border-t border-ink/[0.06] pt-5">
        <OfferRentAmendments offerId={id} />
      </div>
    </div>
  )
}

const StatusRejected = () => {
  const { t } = useTranslation('offers')
  return (
    <p className="m-0 text-sm text-muted-foreground">
      {t('actions.offer_rejected')}
    </p>
  )
}

const StatusTerminated = () => {
  const { t } = useTranslation('offers')
  return (
    <p className="m-0 text-sm text-muted-foreground">
      {t('actions.contract_terminated')}
    </p>
  )
}

const COMPONENTS: Record<
  string,
  React.ComponentType<{ id: string; hasInspection?: boolean }>
> = {
  CREATED: StatusCreate,
  INTERESTED: StatusInterested,
  ACCEPTED: StatusAccepted,
  RESERVED: StatusReserved,
  REJECTED: StatusRejected,
  ONGOING: StatusOngoing,
  TERMINATED: StatusTerminated
}

export function OfferActions({ status, id, hasInspection }: OfferActionProps) {
  const { t } = useTranslation('offers')
  const Component = COMPONENTS[status]

  return (
    <Card className="flex flex-col gap-5 p-6">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Activity className="h-5 w-5" />
        </span>
        <h3 className="m-0 font-display text-lg font-bold tracking-tight text-ink">
          {t('admin.actions.offer_status')}
        </h3>
      </div>

      <div className="py-2">
        <StatusStepper status={status} />
      </div>

      <div className="mt-auto border-t border-ink/[0.06] pt-5">
        {Component && <Component id={id} hasInspection={hasInspection} />}
      </div>
    </Card>
  )
}
